// src/pages/Campaigns.tsx — v2 (IDC-01, Nov 3, 2026): "Mail this route" on a route campaign (Prices & approval tab):
//   ops_mail_it('campaign') builds a GM-MARKET-PO draft and send-procurement-rfq emails the print vendor; the quote then
//   comes back to Today → Supplier orders. Neighbor-flyer campaigns are refused here (they mail from Neighbor flyers).
// Campaigns: every campaign grouped Active / Draft / Ended with what it produced
// (OPS-FINAL D5 results, kept), plus the GE-03 lock and approval views. The wizard lives at /campaigns/new.
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ACK_LABEL,
  canApply,
  CHANNEL_LABEL,
  ops,
  PURPOSE_LABEL,
  type CampaignDetail,
  type CampaignResult,
  type ActivityRow,
  type CampaignReview,
  type FunnelStage,
  type PrintVendor,
} from "../lib/ops";
import { count, money, parseDollars, pct1, scanRateTone, shortDate, toDollarText } from "../lib/format";
import { useOperator, useToast } from "../lib/context";
import { Chip, Loading, Notice, PageFrame, SectionLabel } from "../components/ui";
import { Panel } from "../components/Panel";
import { LockCard } from "../components/LockCard";

const STAGES: FunnelStage[] = ["scan", "engage", "price_view", "checkout_start", "submit", "pay"];
const STAGE_LABEL: Record<FunnelStage, string> = {
  scan: "Scanned",
  engage: "Engaged",
  price_view: "Saw a price",
  checkout_start: "Started checkout",
  submit: "Submitted",
  pay: "Paid",
};

const GROUPS: { id: string; label: string; match: (r: CampaignResult) => boolean }[] = [
  { id: "active", label: "Active", match: (r) => r.status === "active" },
  { id: "draft", label: "Draft", match: (r) => r.status === "draft" },
  { id: "ended", label: "Ended", match: (r) => r.status === "ended" || r.status === "archived" },
];

export default function Campaigns() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { role } = useOperator();
  const [rows, setRows] = useState<CampaignResult[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const selected = params.get("campaign");

  const load = useCallback(async () => {
    try {
      setRows(await ops.campaignResults());
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  const open = (id: string | null) => {
    const next = new URLSearchParams();
    if (id) next.set("campaign", id);
    setParams(next, { replace: true });
  };

  const sel = rows?.find((r) => r.id === selected) ?? null;
  const panel =
    sel && !sel.unattributed ? (
      <CampaignPanel key={sel.id} row={sel} onClose={() => open(null)} onChanged={load} />
    ) : undefined;
  const campaigns = rows?.filter((r) => !r.unattributed) ?? [];
  const unattributed = rows?.filter((r) => r.unattributed) ?? [];

  return (
    <PageFrame title="Campaigns" lede="Every campaign, and what each one produced from first scan to paid booking." panel={panel}>
      <div className="toolbar">
        <DiscountSwitch />
        {canApply(role) && (
          <div className="toolbar-actions">
            <button type="button" className="btn primary" onClick={() => navigate("/campaigns/new")}>
              New campaign →
            </button>
          </div>
        )}
      </div>
      {error && <Notice kind="error">{error}</Notice>}
      {!rows && !error && <Loading what="campaigns" />}
      {rows && campaigns.length === 0 && <p className="empty-state">No campaigns yet.</p>}
      {rows &&
        GROUPS.map((g) => {
          const list = campaigns.filter(g.match);
          if (list.length === 0) return null;
          return (
            <section key={g.id} className="campaign-group" aria-label={g.label}>
              <SectionLabel>
                {g.label} ({list.length})
              </SectionLabel>
              <ResultsTable rows={list} selected={selected} onOpen={open} showFunnel={g.id !== "draft"} />
            </section>
          );
        })}
      {rows && unattributed.length > 0 && (
        <section className="campaign-group" aria-label="Unattributed">
          <ResultsTable rows={unattributed} selected={selected} onOpen={open} showFunnel={false} />
        </section>
      )}
    </PageFrame>
  );
}

function ResultsTable(props: { rows: CampaignResult[]; selected: string | null; onOpen: (id: string) => void; showFunnel: boolean }) {
  return (
    <div className="table-scroll">
      <table className="data-table">
        <thead>
          <tr>
            <th scope="col">Campaign</th>
            <th scope="col">ZIP</th>
            <th scope="col">Window</th>
            <th scope="col" className="num">Scans</th>
            <th scope="col" className="num">Paid bookings</th>
            <th scope="col" className="num">Paid revenue</th>
          </tr>
        </thead>
        <tbody>
          {props.rows.map((r) => {
            const clickable = !r.unattributed;
            return (
              <tr
                key={r.id}
                className={clickable ? (props.selected === r.id ? "row-link selected" : "row-link") : "row-quiet"}
                tabIndex={clickable ? 0 : undefined}
                onClick={clickable ? () => props.onOpen(r.id) : undefined}
                onKeyDown={
                  clickable
                    ? (e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          props.onOpen(r.id);
                        }
                      }
                    : undefined
                }
                aria-label={clickable ? `Open ${r.name}` : undefined}
              >
                <td data-label="Campaign">
                  <span className="cell-main">{r.name}</span>
                  {r.unattributed ? (
                    <span className="cell-sub">Events with no campaign, or one we don't recognise</span>
                  ) : (
                    <>
                      {(r.slogan || r.slug) && (
                        <span className="cell-sub">
                          {r.slogan ?? ""}
                          {r.slogan && r.slug ? ", " : ""}
                          {r.slug ? <span className="mono">/go/{r.slug}</span> : null}
                        </span>
                      )}
                      <span className="chips campaign-chips">
                        <Chip tone={r.purpose ? "info" : "muted"}>{r.purpose ? PURPOSE_LABEL[r.purpose] : "Rhythm"}</Chip>
                        {r.channel && <Chip tone="muted">{CHANNEL_LABEL[r.channel] ?? r.channel}</Chip>}
                        {(r.lock_count ?? 0) > 0 && (
                          <Chip tone="muted">
                            {r.lock_count} {r.lock_count === 1 ? "price lock" : "price locks"}
                          </Chip>
                        )}
                      </span>
                      {props.showFunnel && <FunnelBar funnel={r.funnel} />}
                    </>
                  )}
                </td>
                <td data-label="ZIP" className="mono">{r.zip ?? "—"}</td>
                <td data-label="Window">{r.start_date ? `${shortDate(r.start_date)} – ${shortDate(r.end_date)}` : "—"}</td>
                <td data-label="Scans" className="num">{count(r.scans)}</td>
                <td data-label="Paid bookings" className="num">{count(r.conversions)}</td>
                <td data-label="Paid revenue" className="num money">{r.unattributed ? "—" : money(r.paid_revenue_cents)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** The PAY-01 switch (decision 11): everyone sees its state; only the owner can change it. */
function DiscountSwitch() {
  const { role } = useOperator();
  const toast = useToast();
  const [on, setOn] = useState<boolean | null>(null);
  const [editing, setEditing] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    ops
      .campaignOptions()
      .then((o) => live && setOn(o.discount_approvals_enabled))
      .catch(() => live && setOn(null));
    return () => {
      live = false;
    };
  }, []);

  if (on === null) return <span />;
  const next = !on;
  async function confirm() {
    setBusy(true);
    setError(null);
    try {
      const r = await ops.setDiscountApprovals(next, reason.trim());
      setOn(r.discount_approvals_enabled);
      setEditing(false);
      setReason("");
      toast(r.discount_approvals_enabled ? "✓ Discount approvals on" : "✓ Discount approvals off");
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="switch-line">
      <p className="toolbar-note">
        {on
          ? "Discounted campaigns can be approved."
          : "Discounted campaigns can't be approved until Maestro payouts follow the rate card (PAY-01). Campaigns without price locks can."}
      </p>
      {role === "owner" && !editing && (
        <button type="button" className="btn quiet" onClick={() => setEditing(true)}>
          {on ? "Turn off discount approvals…" : "Turn on discount approvals…"}
        </button>
      )}
      {role === "owner" && editing && (
        <div className="inline-form">
          <p className="action-title">{on ? "Turn off discount approvals" : "Turn on discount approvals"}</p>
          <p>
            {on
              ? "No campaign with price locks can be approved until you turn this back on. Campaigns already running keep their prices."
              : "You'll be able to approve campaigns with price locks. Turn this on only once Maestros are paid from the rate card."}
          </p>
          <textarea
            className="reason"
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            aria-label="Reason for switching discount approvals"
            placeholder="Why? This goes in the change log."
          />
          {error && <Notice kind="error">{error}</Notice>}
          <div className="row-actions">
            <button type="button" className="btn primary" disabled={busy || reason.trim().length < 5} onClick={confirm}>
              {busy ? "Saving…" : on ? "Turn off discount approvals" : "Turn on discount approvals"}
            </button>
            <button type="button" className="btn quiet" onClick={() => setEditing(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Scan → paid in six steps; each bar's length is its share of scans. */
function FunnelBar({ funnel }: { funnel: Record<FunnelStage, number> }) {
  const top = Math.max(funnel.scan, 1);
  return (
    <span className="funnel-bar" aria-label={STAGES.map((s) => `${STAGE_LABEL[s]} ${funnel[s]}`).join(", ")}>
      {STAGES.map((s) => (
        <span key={s} className="funnel-step" style={{ width: `${Math.min(100, (funnel[s] / top) * 100)}%` }} title={`${STAGE_LABEL[s]}: ${funnel[s]}`} />
      ))}
    </span>
  );
}

type Tab = "funnel" | "routes" | "variants" | "prices";

function CampaignPanel(props: { row: CampaignResult; onClose: () => void; onChanged: () => void | Promise<void> }) {
  const isDraft = props.row.status === "draft";
  const [tab, setTab] = useState<Tab>(isDraft ? "prices" : "funnel");
  const tabs: Tab[] = isDraft ? ["prices"] : ["funnel", "routes", "variants", "prices"];
  const [d, setD] = useState<CampaignDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isDraft) return;
    let live = true;
    ops
      .campaignDetail(props.row.id)
      .then((x) => live && setD(x))
      .catch((e: Error) => live && setError(e.message));
    return () => {
      live = false;
    };
  }, [props.row.id, isDraft]);

  const label = (t: Tab) => (t === "funnel" ? "Funnel" : t === "routes" ? "Routes" : t === "variants" ? "Variants" : "Prices & approval");
  return (
    <Panel kicker={isDraft ? "Draft campaign" : "Campaign"} title={props.row.name} onClose={props.onClose}>
      <div className="tabs" role="tablist" aria-label="Campaign detail">
        {tabs.map((t) => (
          <button key={t} type="button" role="tab" aria-selected={tab === t} className={tab === t ? "tab on" : "tab"} onClick={() => setTab(t)}>
            {label(t)}
          </button>
        ))}
      </div>
      {tab === "prices" && <PricesTab row={props.row} onChanged={props.onChanged} />}
      {tab !== "prices" && !d && !error && <Loading what="the campaign" />}
      {tab !== "prices" && error && <Notice kind="error">{error}</Notice>}
      {d && tab === "funnel" && (
        <>
          <table className="data-table compact">
            <thead>
              <tr>
                <th scope="col">Stage</th>
                <th scope="col" className="num">Events</th>
                <th scope="col" className="num">Drop-off</th>
              </tr>
            </thead>
            <tbody>
              {d.funnel.map((f) => (
                <tr key={f.stage}>
                  <td>{STAGE_LABEL[f.stage]}</td>
                  <td className="num">{count(f.count)}</td>
                  <td className="num">{f.drop_off_pct === null ? "—" : pct1(f.drop_off_pct)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="hint">One visit can record several "engaged" events, so drop-off is shown only where a stage is smaller than the one before.</p>
          <p>
            {d.daily_scans.length === 0
              ? "No scans yet."
              : `Scans on ${d.daily_scans.length} ${d.daily_scans.length === 1 ? "day" : "days"}, most on ${shortDate(
                  [...d.daily_scans].sort((a, b) => b.scans - a.scans)[0].day,
                )}.`}
          </p>
        </>
      )}
      {d && tab === "routes" && (
        <>
          {d.routes.length === 0 ? (
            <p className="empty-state">Route results appear once a campaign's routes are chosen and its QR codes carry the route. Coming soon.</p>
          ) : (
            <>
              <table className="data-table compact">
                <thead>
                  <tr>
                    <th scope="col">Route</th>
                    <th scope="col" className="num">Homes</th>
                    <th scope="col" className="num">Scans</th>
                    <th scope="col" className="num">Scan rate</th>
                  </tr>
                </thead>
                <tbody>
                  {d.routes.map((r) => {
                    const tone = scanRateTone(r.scan_rate_pct);
                    return (
                      <tr key={r.carrier_route}>
                        <td className="mono">{r.carrier_route}</td>
                        <td className="num">{count(r.residences)}</td>
                        <td className="num">{r.scans === null ? "—" : count(r.scans)}</td>
                        <td className="num">{r.scan_rate_pct === null ? "—" : <span className={`rate ${tone ?? ""}`}>{pct1(r.scan_rate_pct)}</span>}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {!d.routes_tracked && <p className="hint">Scans per route appear once QR codes carry the route.</p>}
            </>
          )}
        </>
      )}
      {d && tab === "variants" && (
        <>
          {d.variants.length === 0 ? (
            <p className="empty-state">This campaign has no variants.</p>
          ) : (
            <>
              <table className="data-table compact">
                <thead>
                  <tr>
                    <th scope="col">Variant</th>
                    <th scope="col" className="num">Scans</th>
                    <th scope="col" className="num">Paid</th>
                    <th scope="col" className="num">Conversion</th>
                  </tr>
                </thead>
                <tbody>
                  {d.variants.map((v) => (
                    <tr key={v.variant} className={d.variant_winner === v.variant ? "winner" : undefined}>
                      <td className="mono">{v.variant.toUpperCase()}</td>
                      <td className="num">{count(v.scans)}</td>
                      <td className="num">{count(v.conversions)}</td>
                      <td className="num">{pct1(v.conversion_pct)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p>
                {d.variant_winner
                  ? `Variant ${d.variant_winner.toUpperCase()} is winning.`
                  : `No winner yet: each variant needs at least ${d.variant_min_scans} scans.`}
              </p>
            </>
          )}
        </>
      )}
    </Panel>
  );
}

/** Locks with their margins, the approval record, and (owner, active) ending the campaign. */
function PricesTab(props: { row: CampaignResult; onChanged: () => void | Promise<void> }) {
  const { role } = useOperator();
  const toast = useToast();
  const navigate = useNavigate();
  const [rv, setRv] = useState<CampaignReview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ending, setEnding] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [endError, setEndError] = useState<string | null>(null);
  const [endedBy, setEndedBy] = useState<ActivityRow | null>(null);

  useEffect(() => {
    if (props.row.status !== "ended") return;
    let live = true;
    ops
      .activityLog(20, "campaigns", `campaign:${props.row.id}`)
      .then((rows) => live && setEndedBy(rows.find((r) => r.policy === "campaign_end") ?? null))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [props.row.id, props.row.status]);

  const [reloads, setReloads] = useState(0);
  useEffect(() => {
    let live = true;
    ops
      .campaignReview(props.row.id)
      .then((x) => live && setRv(x))
      .catch((e: Error) => live && setError(e.message));
    return () => {
      live = false;
    };
  }, [props.row.id, reloads]);

  async function end() {
    setBusy(true);
    setEndError(null);
    try {
      await ops.endCampaign(props.row.id, reason.trim());
      toast(`✓ ${props.row.name} — Ended`);
      await props.onChanged();
    } catch (e) {
      setEndError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (error) return <Notice kind="error">{error}</Notice>;
  if (!rv) return <Loading what="price locks" />;
  const c = rv.campaign;
  return (
    <>
      {c.status === "draft" && (
        <>
          <p>This campaign is a draft. Its price locks don't change any customer price until the owner approves it.</p>
          {canApply(role) && (
            <button type="button" className="btn primary" onClick={() => navigate(`/campaigns/new?draft=${encodeURIComponent(c.id)}`)}>
              Continue in the wizard →
            </button>
          )}
        </>
      )}
      {endedBy && (
        <p className="hint">
          {endedBy.changed_by_email === "system (nightly)"
            ? `Ended automatically on ${shortDate(endedBy.changed_at.slice(0, 10))}, the night after its window closed.`
            : `Ended by ${endedBy.changed_by_email ?? "an operator"} on ${shortDate(endedBy.changed_at.slice(0, 10))}: “${endedBy.reason}”`}
        </p>
      )}
      {c.approved_at && (
        <p className="hint">
          Approved {shortDate(c.approved_at.slice(0, 10))}. Acknowledged warnings are kept with the approval in the change log.
        </p>
      )}
      <SectionLabel>Price locks</SectionLabel>
      {rv.locks.length === 0 ? (
        <p>No price locks. Customers see standard prices.</p>
      ) : (
        rv.locks.map((l) => <LockCard key={`${l.service_id}|${l.zip}`} lock={l} />)
      )}
      {rv.acks_required.length > 0 && c.status === "draft" && (
        <p className="hint warn">
          {rv.acks_required.length} {rv.acks_required.length === 1 ? "lock needs" : "locks need"} an acknowledgment at approval:{" "}
          {rv.acks_required.map((a) => `${a.service_name} (${a.reasons.map((x) => ACK_LABEL[x]).join("; ")})`).join(", ")}.
        </p>
      )}
      <SectionLabel>Mail</SectionLabel>
      <p>
        {rv.totals.routes === 0
          ? "No mail routes chosen yet."
          : `${count(rv.totals.routes)} ${rv.totals.routes === 1 ? "route" : "routes"}, ${count(rv.totals.addresses)} addresses.`}
      </p>
      <MailCost
        campaignId={c.id}
        costCents={rv.totals.cost_cents}
        breakEven={rv.totals.break_even_conversions}
        onSaved={() => setReloads((n) => n + 1)}
      />
      {canApply(role) && (c.status === "active" || c.status === "draft") && rv.totals.routes > 0 && (
        <MailRoute campaignId={c.id} onSent={async (msg) => { toast(msg); await props.onChanged(); }} />
      )}
      {c.status === "active" && role === "owner" && !ending && (
        <button type="button" className="btn" onClick={() => setEnding(true)}>
          End this campaign…
        </button>
      )}
      {c.status === "active" && role === "owner" && ending && (
        <div className="inline-form">
          <p className="action-title">End {c.name}</p>
          <p>Customers stop seeing its promo prices right away. Its QR code keeps working and shows standard prices.</p>
          <textarea
            className="reason"
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            aria-label="Reason for ending the campaign"
            placeholder="Why end it now? This goes in the change log."
          />
          {endError && <Notice kind="error">{endError}</Notice>}
          <div className="row-actions">
            <button type="button" className="btn danger" disabled={busy || reason.trim().length < 5} onClick={end}>
              {busy ? "Ending…" : "End campaign"}
            </button>
            <button type="button" className="btn quiet" onClick={() => setEnding(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </>
  );
}

// IDC-01: send this route's mail job to a print/mail vendor (quote request through the supplier-order flow).
function MailRoute(props: { campaignId: string; onSent: (msg: string) => Promise<void> }) {
  const [vendors, setVendors] = useState<PrintVendor[] | null>(null);
  const [vendor, setVendor] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open || vendors) return;
    ops.printVendors().then((v) => { setVendors(v); if (v.length === 1) setVendor(v[0].supplier_id); }).catch((e: Error) => setErr(e.message));
  }, [open, vendors]);
  async function go() {
    if (busy || !vendor) return; setBusy(true); setErr(null);
    try {
      const built = await ops.mailIt("campaign", props.campaignId, vendor);
      const sent = await ops.sendRfq(built.order_id);
      await props.onSent(`✓ Quote request sent to ${sent.sent_to}${sent.test_mode ? " (TEST)" : ""} · ${built.po_number} · ${built.pieces} pieces`);
      setOpen(false);
    } catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  }
  if (!open) return <button type="button" className="btn" onClick={() => setOpen(true)}>Mail this route…</button>;
  const chosen = vendors?.find((v) => v.supplier_id === vendor) ?? null;
  return (
    <div className="inline-form">
      <p className="action-title">Mail this route</p>
      {err && <Notice kind="error">{err}</Notice>}
      {!vendors && !err && <Loading what="print vendors" />}
      {vendors && vendors.length === 0 && <p className="hint">No print/mail vendor is set up yet.</p>}
      {vendors && vendors.length > 0 && (
        <>
          <select value={vendor} onChange={(e) => setVendor(e.target.value)} aria-label="Print vendor">
            <option value="">Choose a vendor…</option>
            {vendors.map((v) => <option key={v.supplier_id} value={v.supplier_id}>{v.name}{v.test_mode ? " (test mode)" : ""}</option>)}
          </select>
          <p className="hint">Builds a print order (PO GM-MARKET-PO-…) for this route's pieces (addressed: every mailable house except the holdouts) and emails the vendor a quote request.
            {chosen?.test_mode ? " Test mode: the email goes only to the test address." : ""} The vendor's quote comes back to Today → Supplier orders.</p>
        </>
      )}
      <div className="row-actions">
        <button type="button" className="btn primary" disabled={busy || !vendor} onClick={() => void go()}>{busy ? "Sending…" : "Request a quote"}</button>
        <button type="button" className="btn quiet" disabled={busy} onClick={() => setOpen(false)}>Cancel</button>
      </div>
    </div>
  );
}

// OPS-METRICS-01: print + postage. Feeds the route board ("pays its own mail") and the break-even bookings above.
function MailCost(props: { campaignId: string; costCents: number | null; breakEven: number | null; onSaved: () => void }) {
  const { role } = useOperator();
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(toDollarText(props.costCents));
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cents = parseDollars(text);

  async function save() {
    if (cents === null) return;
    setBusy(true);
    setError(null);
    try {
      await ops.setCampaignCost(props.campaignId, cents, reason.trim());
      toast(`✓ Mail cost saved: ${money(cents)}`);
      setEditing(false);
      setReason("");
      props.onSaved();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <p>
        {props.costCents === null ? (
          <span className="muted">Print and postage not recorded yet, so its routes can't be scored on paying back their mail.</span>
        ) : (
          <>
            Print and postage: {money(props.costCents)}
            {props.breakEven !== null ? ` · pays for itself after ${count(props.breakEven)} ${props.breakEven === 1 ? "booking" : "bookings"} at the average margin` : ""}
          </>
        )}
      </p>
      {canApply(role) && !editing && (
        <button type="button" className="btn" onClick={() => setEditing(true)}>
          {props.costCents === null ? "Record the mail cost…" : "Change the mail cost…"}
        </button>
      )}
      {canApply(role) && editing && (
        <div className="inline-form">
          <div className="field">
            <label htmlFor="mail-cost">Print + postage (dollars)</label>
            <input id="mail-cost" inputMode="decimal" value={text} onChange={(e) => setText(e.target.value)} placeholder="e.g. 197.54" />
          </div>
          <textarea
            className="reason"
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            aria-label="Reason"
            placeholder="Where the number comes from (invoice, estimate). This goes in the change log."
          />
          {error && <Notice kind="error">{error}</Notice>}
          <div className="row-actions">
            <button type="button" className="btn primary" disabled={busy || cents === null || reason.trim().length < 5} onClick={save}>
              {busy ? "Saving…" : "Save mail cost"}
            </button>
            <button type="button" className="btn quiet" onClick={() => { setEditing(false); setError(null); setText(toDollarText(props.costCents)); }}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </>
  );
}
